import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';
import UniversalModal from '../Common/UniversalModal';
import UnsavedChangesPopover from '../Common/UnsavedChangesPopover';
import Pagination from '../Common/Pagination';
import Toast from '../Common/Toast';
import { useAuth } from '../../context/AuthContext';
import './Lots.css';
import '../Common/CardActions.css';
import '../Common/PrimaryActionButton.css';
import OwnerBadge from '../Common/OwnerBadge';
import LotForm from './LotForm';

const LOTS_COL_WIDTHS_KEY = 'aot_lots_table_col_widths_v1';
const LOT_CATEGORY_LABELS = {
  real_estate: 'Недвижимость',
  transport: 'Транспорт',
  equipment: 'Оборудование',
  debt: 'Дебиторка',
  other: 'Прочее',
};
const LOTS_COLLATOR = new Intl.Collator('ru', {
  numeric: true,
  sensitivity: 'base',
});

const readColWidths = () => {
  const defaults = {
    name: 420,
    category: 160,
    start_price: 170,
    status: 190,
    created_date: 120,
    actions: 110,
  };
  try {
    const raw = localStorage.getItem(LOTS_COL_WIDTHS_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return defaults;
    const out = { ...defaults };
    Object.keys(defaults).forEach((k) => {
      const v = parsed[k];
      if (typeof v === 'number' && Number.isFinite(v) && v >= 60 && v <= 900) {
        out[k] = v;
      }
    });
    return out;
  } catch (_) {
    return defaults;
  }
};

const parseLotNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

const sortLotsByNumber = (items) => {
  return [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    const aNumber = parseLotNumber(a?.lot_number);
    const bNumber = parseLotNumber(b?.lot_number);
    if (aNumber !== null && bNumber !== null && aNumber !== bNumber) return aNumber - bNumber;
    if (aNumber !== null && bNumber === null) return -1;
    if (aNumber === null && bNumber !== null) return 1;
    return Number(a?.id || 0) - Number(b?.id || 0);
  });
};

const LotsList = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_staff || !!user?.is_superuser || String(user?.role || '').toLowerCase() === 'admin';

  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: null, // 'create', 'view', 'edit'
    lot: null
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [formData, setFormData] = useState({
    lot_number: '',
    name: '',
    description: '',
    start_price: '',
    category: 'real_estate',
    efrsb_classifiers: [],
    pledge_creditor_satisfaction: false,
    pledge_creditors: [],
  });
  const [toast, setToast] = useState(null);
  const [colWidths, setColWidths] = useState(readColWidths);
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Состояние для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);
  
  // Состояние для диалога подтверждения
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Удалить',
    cancelText: 'Отмена'
  });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    fetchLots();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiService.getUiSettings();
        const v = Number(resp?.data?.table_page_size);
        if ([12, 24, 48, 96].includes(v)) {
          setItemsPerPage(v);
          setCurrentPage(1);
        }
      } catch (_) {
        // ignore
      }
    })();
  }, []);

  const fetchLots = async () => {
    try {
      setLoading(true);
      setError('');
      const all = [];
      let page = 1;
      while (true) {
        const response = await apiService.getLots(`?page=${page}&page_size=96`);
        const pageResults = response.data?.results || response.data || [];
        all.push(...pageResults);
        if (!response.data?.next) break;
        page += 1;
        if (page > 200) break;
      }
      setLots(sortLotsByNumber(all));
    } catch (err) {
      console.error('Error fetching lots:', err);
      setError('Ошибка загрузки лотов: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      lot_number: '',
      name: '',
      description: '',
      start_price: '',
      category: 'real_estate',
      efrsb_classifiers: [],
    });
    setHasUnsavedChanges(false);
  };
  
  const handleCancelModal = () => {
    if (hasUnsavedChanges && modalState.mode !== 'view') {
      showConfirmDialog(
        'Несохраненные изменения',
        'У вас есть несохраненные изменения. Вы уверены, что хотите закрыть окно? Все изменения будут потеряны.',
        () => {
          setModalState({ isOpen: false, mode: null, lot: null });
          setHasUnsavedChanges(false);
          resetForm();
        },
        'Закрыть без сохранения',
        'Отмена'
      );
    } else {
      setModalState({ isOpen: false, mode: null, lot: null });
      setHasUnsavedChanges(false);
      resetForm();
    }
  };

  const closeLotModal = () => {
    setModalState({ isOpen: false, mode: null, lot: null });
    setHasUnsavedChanges(false);
    resetForm();
  };

  const handleAddLot = () => {
    resetForm();
    setModalState({ isOpen: true, mode: 'create', lot: null });
  };

  const handleViewLot = (lot) => {
    (async () => {
      try {
        setHasUnsavedChanges(false);
        const resp = await apiService.getLot(lot.id);
        setModalState({ isOpen: true, mode: 'view', lot: resp.data });
      } catch (err) {
        setError('Ошибка загрузки лота: ' + (err.response?.data?.detail || err.message));
      }
    })();
  };

  const handleEditLot = (lot) => {
    (async () => {
      try {
        setHasUnsavedChanges(false);
        const resp = await apiService.getLot(lot.id);
        setModalState({ isOpen: true, mode: 'edit', lot: resp.data });
      } catch (err) {
        setError('Ошибка загрузки лота: ' + (err.response?.data?.detail || err.message));
      }
    })();
  };

  const handleExitEditMode = () => {
    if (modalState.mode !== 'edit') return;
    if (hasUnsavedChanges) {
      showConfirmDialog(
        'Несохраненные изменения',
        'У вас есть несохраненные изменения. Выйти из режима редактирования без сохранения?',
        () => {
          closeLotModal();
        },
        'Выйти без сохранения',
        'Отмена'
      );
      return;
    }
    closeLotModal();
  };

  const handleSaveLot = async (payload, { exit } = {}) => {
    try {
      const preparedPayload = {
        ...payload,
        lot_number: parseLotNumber(payload.lot_number),
        start_price: payload.start_price ? parseFloat(payload.start_price) : 0,
        category: payload.category || 'real_estate',
        efrsb_classifiers: Array.isArray(payload?.efrsb_classifiers)
          ? payload.efrsb_classifiers.map((id) => Number(id)).filter((id) => Number.isFinite(id))
          : [],
        pledge_creditor_satisfaction: !!payload.pledge_creditor_satisfaction,
        pledge_creditors: Array.isArray(payload.pledge_creditors) ? payload.pledge_creditors : [],
      };

      if (Number.isNaN(preparedPayload.start_price)) {
        preparedPayload.start_price = 0;
      }

      if (modalState.lot && modalState.lot.auction) {
        preparedPayload.auction = modalState.lot.auction;
      }

      let savedLot = null;

      if (modalState.lot) {
        const response = await apiService.updateLot(modalState.lot.id, preparedPayload);
        savedLot = response.data;
        setLots(sortLotsByNumber(lots.map(lot => lot.id === modalState.lot.id ? savedLot : lot)));
      } else {
        const response = await apiService.createLot(preparedPayload);
        savedLot = response.data;
        setLots(sortLotsByNumber([savedLot, ...lots]));
      }

      setHasUnsavedChanges(false);

      showToast(modalState.lot ? 'Изменения сохранены' : 'Лот создан');

      if (exit) {
        setModalState({ isOpen: false, mode: null, lot: null });
        resetForm();
      } else {
        setModalState({ isOpen: true, mode: 'edit', lot: savedLot });
      }

      fetchLots();
      return savedLot;
    } catch (error) {
      const data = error.response?.data;
      let message = data?.detail || error.message;
      if (data && typeof data === 'object') {
        const firstEntry = Object.entries(data)[0];
        if (firstEntry) {
          const [, value] = firstEntry;
          message = Array.isArray(value) ? value.join(', ') : String(value || message);
        }
      }
      const errorMessage = 'Ошибка сохранения лота: ' + message;
      setError(errorMessage);
      showToast(errorMessage, 'error');
      console.error('Error saving lot:', error);
      return false;
    }
  };

  const handleDeleteLot = async (lotId) => {
    const lot = lots.find(l => l.id === lotId);
    if (!lot) return;
    
    showConfirmDialog(
      'Подтверждение удаления',
      `Вы уверены, что хотите удалить лот ${lot.name}? Это действие необратимо.`,
      async () => {
        try {
          await apiService.deleteLot(lotId);
          setLots(lots.filter(lot => lot.id !== lotId));
        } catch (error) {
          setError('Ошибка удаления лота: ' + (error.response?.data?.detail || error.message));
          console.error('Error deleting lot:', error);
        }
      }
    );
  };

  // Функции для управления диалогом подтверждения
  const showConfirmDialog = (title, message, onConfirm, confirmText = 'Удалить', cancelText = 'Отмена') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      confirmText,
      cancelText
    });
  };

  const hideConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  };

  const handleConfirmAction = () => {
    if (confirmDialog.onConfirm) {
      confirmDialog.onConfirm();
    }
    hideConfirmDialog();
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB'
    }).format(price);
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('ru-RU');
  };

  const getCategoryText = (category) => {
    return LOT_CATEGORY_LABELS[category] || category;
  };

  const isLotAttached = (auctionInfo) => {
    return auctionInfo && 
           auctionInfo !== 'null' && 
           typeof auctionInfo === 'string' && 
           !auctionInfo.includes('<method-wrapper') &&
           auctionInfo.trim() !== '';
  };

  const getAuctionInfo = (auctionInfo) => {
    if (isLotAttached(auctionInfo)) {
      return auctionInfo;
    }
    return 'Свободен';
  };

  const persistColWidths = useCallback((next) => {
    if (!isAdmin) return;
    try {
      localStorage.setItem(LOTS_COL_WIDTHS_KEY, JSON.stringify(next || {}));
    } catch (_) {}
  }, [isAdmin]);

  const startResize = useCallback((colKey, e) => {
    if (!isAdmin) return;
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startWidth = Number(colWidths?.[colKey] || 0) || 0;
    const minW = 80;
    const maxW = 900;

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const nextW = Math.min(maxW, Math.max(minW, startWidth + dx));
      setColWidths((prev) => ({ ...(prev || {}), [colKey]: nextW }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setColWidths((prev) => {
        persistColWidths(prev);
        return prev;
      });
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [colWidths, isAdmin, persistColWidths]);

  const handleSort = useCallback((key) => {
    setSortConfig((previous) => ({
      key,
      direction: previous.key === key && previous.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  }, []);

  const thWithResizer = useCallback((colKey, style, label, className = '', sortable = true) => {
    const w = colKey ? colWidths?.[colKey] : null;
    const isSorted = sortable && sortConfig.key === colKey;
    return (
      <th
        className={className}
        style={{ ...(style || {}), ...(w ? { width: w } : {}) }}
        aria-sort={isSorted ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : undefined}
      >
        {sortable ? (
          <button
            type="button"
            className={`lots-sort-button${isSorted ? ' lots-sort-button--active' : ''}`}
            onClick={() => handleSort(colKey)}
            title={`Сортировать: ${label}`}
          >
            <span>{label}</span>
            <span className="lots-sort-indicator" aria-hidden="true">
              {isSorted ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
            </span>
          </button>
        ) : (
          <span className="lots-th-label">{label}</span>
        )}
        {isAdmin && colKey && (
          <span
            className="col-resizer"
            role="presentation"
            onMouseDown={(e) => startResize(colKey, e)}
          />
        )}
      </th>
    );
  }, [colWidths, handleSort, isAdmin, sortConfig, startResize]);

  // Обработчики пагинации
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении количества элементов
    apiService.saveUiSettings({ table_page_size: newItemsPerPage }).catch(() => {});
  };

  const filtered = React.useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return lots;
    return (lots || []).filter((lot) => {
      const hay = `${lot.name || ''} ${lot.description || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [lots, search]);

  const sorted = React.useMemo(() => {
    if (!sortConfig.key) return filtered;

    const getSortValue = (lot) => {
      switch (sortConfig.key) {
        case 'name':
          return String(lot?.name || '');
        case 'category':
          return LOT_CATEGORY_LABELS[lot?.category] || String(lot?.category || '');
        case 'start_price': {
          const price = Number(lot?.start_price);
          return Number.isFinite(price) ? price : null;
        }
        case 'status':
          return lot?.auction_info
            && lot.auction_info !== 'null'
            && typeof lot.auction_info === 'string'
            && !lot.auction_info.includes('<method-wrapper')
            && lot.auction_info.trim() !== ''
            ? lot.auction_info
            : 'Свободен';
        case 'created_date': {
          const timestamp = Date.parse(lot?.created_date);
          return Number.isFinite(timestamp) ? timestamp : null;
        }
        default:
          return '';
      }
    };

    const direction = sortConfig.direction === 'desc' ? -1 : 1;
    return filtered
      .map((lot, index) => ({ lot, index }))
      .sort((left, right) => {
        const leftValue = getSortValue(left.lot);
        const rightValue = getSortValue(right.lot);
        const leftEmpty = leftValue === null || leftValue === '';
        const rightEmpty = rightValue === null || rightValue === '';

        if (leftEmpty !== rightEmpty) return leftEmpty ? 1 : -1;
        if (leftEmpty && rightEmpty) return left.index - right.index;

        const comparison = typeof leftValue === 'number' && typeof rightValue === 'number'
          ? leftValue - rightValue
          : LOTS_COLLATOR.compare(String(leftValue), String(rightValue));
        return comparison === 0 ? left.index - right.index : comparison * direction;
      })
      .map(({ lot }) => lot);
  }, [filtered, sortConfig]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, itemsPerPage, lots]);

  const totalItemsCount = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalItemsCount / itemsPerPage));
  const paginated = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sorted.slice(start, start + itemsPerPage);
  }, [sorted, currentPage, itemsPerPage]);

  return (
    <div className="lots-list-container">
      <div className="page-header">
        <h2>Лоты</h2>
        <button className="primary-create-button" onClick={handleAddLot}>
          <span className="primary-create-button__icon">+</span>
          Создать лот
        </button>
      </div>

      <div className="lots-search-row">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по лотам..."
        />
        <div className="lots-search-count">
          Найдено: <b>{totalItemsCount}</b>
        </div>
      </div>

      {loading && (
        <div className="loading">Загрузка...</div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="lots-table-wrapper">
      {!loading && paginated.length === 0 ? (
        <div className="empty-state">
          <h3>Лоты не найдены</h3>
          <p>{search.trim() ? 'Попробуйте изменить запрос поиска' : 'Создайте первый лот, нажав кнопку "Создать лот"'}</p>
        </div>
      ) : (
          <table className="lots-table">
            <colgroup>
              <col style={{ width: colWidths?.name }} />
              <col style={{ width: colWidths?.category }} />
              <col style={{ width: colWidths?.start_price }} />
              <col style={{ width: colWidths?.status }} />
              <col style={{ width: colWidths?.created_date }} />
              <col style={{ width: colWidths?.actions }} />
            </colgroup>
            <thead>
              <tr>
                {thWithResizer('name', null, 'Наименование')}
                {thWithResizer('category', null, 'Категория', 'lots-col-category')}
                {thWithResizer('start_price', null, 'Начальная цена', 'lots-col-start-price')}
                {thWithResizer('status', null, 'Статус')}
                {thWithResizer('created_date', null, 'Создан')}
                {thWithResizer('actions', null, 'Действия', '', false)}
              </tr>
            </thead>
            <tbody>
              {paginated.map((lot) => (
                <tr key={lot.id} className="lots-row" onClick={() => handleViewLot(lot)}>
                  <td>
                    <div className="lots-name-cell">
                      <div className="lots-name">{lot.name}</div>
                      <OwnerBadge email={lot.owner_email} />
                      {lot.description && (
                        <div className="lots-desc">
                          {lot.description.length > 120 ? `${lot.description.substring(0, 120)}...` : lot.description}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="lots-col-category">
                    <span className={`category-badge ${lot.category}`}>
                      {getCategoryText(lot.category)}
                    </span>
                  </td>
                  <td className="lots-nowrap lots-col-start-price">{formatPrice(lot.start_price)}</td>
                  <td className="lots-status-cell">
                    <span className={`status-badge ${isLotAttached(lot.auction_info) ? 'attached' : 'free'}`}>
                      {getAuctionInfo(lot.auction_info)}
                    </span>
                  </td>
                  <td className="lots-nowrap">{formatDate(lot.created_date)}</td>
                  <td>
                    <div className="card-actions" style={{ justifyContent: 'flex-start' }}>
                      <button
                        type="button"
                        className="card-action-btn card-action-btn--edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditLot(lot);
                        }}
                        title="Редактировать"
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className="card-action-btn card-action-btn--delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLot(lot.id);
                        }}
                        title="Удалить"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      {/* Пагинация */}
      {totalItemsCount > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          totalItems={totalItemsCount}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      {/* Модальное окно для создания/просмотра лота */}
      {modalState.isOpen && (
        <UniversalModal
          isOpen={modalState.isOpen}
          onClose={handleCancelModal}
          title={
            modalState.mode === 'create'
              ? 'Создание лота'
              : modalState.mode === 'edit'
                ? 'Редактирование лота'
                : modalState.lot
                  ? `Лот: ${modalState.lot.name}`
                  : 'Просмотр лота'
          }
          isViewMode={modalState.mode === 'view'}
          onEdit={modalState.mode === 'view' ? () => handleEditLot(modalState.lot) : undefined}
          disableEscClose={modalState.mode === 'edit'}
          modalClassName="universal-modal--wide"
        >
          <LotForm
            lot={modalState.lot}
            formData={formData}
            setFormData={setFormData}
            initialLotNumber={formData?.lot_number}
            onSave={handleSaveLot}
            onCancel={handleCancelModal}
            isViewMode={modalState.mode === 'view'}
            onFormChange={setHasUnsavedChanges}
            isLotAttached={isLotAttached}
            getAuctionInfo={getAuctionInfo}
            isConfirmOpen={confirmDialog.isOpen}
            onExitEdit={handleExitEditMode}
          />
        </UniversalModal>
      )}

      {/* Диалог подтверждения */}
      {confirmDialog.isOpen && (
        <ConfirmDialog 
          confirmDialog={confirmDialog}
          onCancel={hideConfirmDialog}
          onConfirm={handleConfirmAction}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
};

// Компонент диалога подтверждения
const ConfirmDialog = ({ confirmDialog, onCancel, onConfirm }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        // Блокируем распространение события
        e.preventDefault();
        e.stopPropagation(); 
        e.stopImmediatePropagation();
        // Закрываем диалог (как если бы нажали "Отмена")
        onCancel();
        return false;
      }
    };

    // Используем захватывающую фазу с максимальным приоритетом
    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [onCancel]);

  return (
    <UnsavedChangesPopover
      isOpen={!!confirmDialog?.isOpen}
      title={confirmDialog?.title || 'Подтверждение'}
      message={confirmDialog?.message}
      onCancel={onCancel}
      onConfirm={onConfirm}
      cancelText={confirmDialog?.cancelText || 'Отмена'}
      confirmText={confirmDialog?.confirmText || 'Подтвердить'}
      zIndex={10000}
    />
  );
};

export default LotsList;
